import {
	getMetadataResolved,
	getCountResolved,
	renameCount,
	setCountIsLoading,
	setMetadataIsLoading,
	renameMetadata,
	setAttributes,
	updateAttributeValuesDataGrid,
	setAttributeDataGrid,
	setAttributeRangeValues,
	setAttributeRangeUnitValues,
	updateAttribute,
	updateAttributeExcludedDataGrid,
} from '@Actions';
import { createReducer } from '@reduxjs/toolkit';
import { type AttributeOption } from '@Types/automaticSetup.types';
import {
	type CountChange,
	type CountRename,
	type MetadataChange,
	type MetadataRename,
	type RiskStoreState,
} from '@Types/risk.types';
import { type AnyAction } from 'redux';

export interface AttributeValueChange {
	index: number;
	value?: string;
	excluded?: boolean;
}

export const initState: RiskStoreState = {
	isCountLoading: [],
	count: {},
	isMetadataLoading: [],
	unitMetadata: {},
	attributes: [],
	attributeDataGrid: [],
	attributeRangeValues: [],
	attributeRangeUnitValues: [],
};

interface RiskReducer {
	[x: string]: (
		state: RiskStoreState,
		action: AnyAction,
	) => RiskStoreState | undefined;
}

export interface IsLoadingChange {
	key: string;
	loading: boolean;
}

const updateCount = (state: RiskStoreState, action: CountChange) => {
	if (action.values || state.count[action.key])
		state.count[action.key] = action.values;
	return state;
};

const copyCount = (state: RiskStoreState, action: CountRename) => {
	if (state.count[action.existingKey]) {
		state.count[action.newKey] = state.count[action.existingKey];
		state.count[action.existingKey] = undefined;
	}
	return state;
};

const updateIsCountLoading = (
	state: RiskStoreState,
	action: IsLoadingChange,
) => {
	if (action.loading && state.isCountLoading.indexOf(action.key) === -1)
		state.isCountLoading.push(action.key);
	else if (!action.loading && state.isCountLoading.indexOf(action.key) !== -1)
		state.isCountLoading = state.isCountLoading.filter(
			value => value !== action.key,
		);
	return state;
};

const updateMetadata = (state: RiskStoreState, action: MetadataChange) => {
	if (action.value || state.count[action.key]) {
		state.unitMetadata[action.key] = action.value;
	}
	return state;
};

const copyMetadata = (state: RiskStoreState, action: MetadataRename) => {
	if (state.unitMetadata[action.existingKey]) {
		state.unitMetadata[action.newKey] =
			state.unitMetadata[action.existingKey];
		state.unitMetadata[action.existingKey] = undefined;
	}
	return state;
};

const updateIsMetadataLoading = (
	state: RiskStoreState,
	action: IsLoadingChange,
) => {
	if (action.loading && state.isMetadataLoading.indexOf(action.key) === -1)
		state.isMetadataLoading.push(action.key);
	else if (
		!action.loading &&
		state.isMetadataLoading.indexOf(action.key) !== -1
	)
		state.isMetadataLoading = state.isMetadataLoading.filter(
			value => value !== action.key,
		);
	return state;
};

const updateAttributes = (state: RiskStoreState, action: AttributeOption) => {
	const index = state.attributes.findIndex(
		attribute => attribute.key === action.key,
	);
	if (index !== -1) state.attributes[index] = action;
	return state;
};

const updateAttributeValues = (
	state: RiskStoreState,
	action: AttributeValueChange[],
) => {
	const rows = state.attributeDataGrid;

	state.attributeDataGrid = rows.map(row => {
		const update = action.find(item => item.index == row.id);
		if (update) return { ...row, value: update.value ?? '' };
		else return row;
	});
	return state;
};

const updateAttributeExcluded = (
	state: RiskStoreState,
	action: AttributeValueChange[],
) => {
	const rows = state.attributeDataGrid;

	state.attributeDataGrid = rows.map(row => {
		const update = action.find(item => item.index == row.id);
		if (update) return { ...row, excluded: update.excluded };
		else return row;
	});
	return state;
};

export const reducer: RiskReducer = {
	[setCountIsLoading.toString()]: (state, action) =>
		updateIsCountLoading(state, action.payload),
	[getCountResolved.toString()]: (state, action) =>
		updateCount(state, action.payload),
	[renameCount.toString()]: (state, action) =>
		copyCount(state, action.payload),
	[setMetadataIsLoading.toString()]: (state, action) =>
		updateIsMetadataLoading(state, action.payload),
	[getMetadataResolved.toString()]: (state, action) =>
		updateMetadata(state, action.payload),
	[renameMetadata.toString()]: (state, action) =>
		copyMetadata(state, action.payload),
	[setAttributes.toString()]: (state, action) => ({
		...state,
		attributes: action.payload,
	}),
	[updateAttribute.toString()]: (state, action) =>
		updateAttributes(state, action.payload),
	[setAttributeDataGrid.toString()]: (state, action) => ({
		...state,
		attributeDataGrid: action.payload,
	}),
	[updateAttributeValuesDataGrid.toString()]: (state, action) =>
		updateAttributeValues(state, action.payload),
	[updateAttributeExcludedDataGrid.toString()]: (state, action) =>
		updateAttributeExcluded(state, action.payload),
	[setAttributeRangeValues.toString()]: (state, action) => ({
		...state,
		attributeRangeValues: action.payload,
	}),
	[setAttributeRangeUnitValues.toString()]: (state, action) => ({
		...state,
		attributeRangeUnitValues: action.payload,
	}),
};

export const riskReducer = createReducer(initState, reducer);
